import React, { useEffect, useRef, useState } from 'react';
import { CancellationPayment } from '../api/payments';
import { WorldpayCheckoutAttempt, WorldpayCheckoutConfig, getCheckoutAttempt, createCheckoutAttempt, payCheckoutAttempt, checkCheckoutAttempt } from '../api/worldpayCheckout';
import { CheckoutInstance, loadWorldpayCheckout } from '../utils/worldpayCheckoutSdk';
import './WorldpayPaymentPanel.css';
import './WorldpayCheckoutPanel.css';

export interface WorldpayPanelProps { payment: CancellationPayment; onClose: () => void; onSettled: (result?: WorldpayCheckoutAttempt) => void }
interface Props extends WorldpayPanelProps { config: WorldpayCheckoutConfig }
const money = (value: number) => new Intl.NumberFormat('en-GB', { style: 'currency', currency: 'GBP' }).format(value);
const terminal = (attempt: WorldpayCheckoutAttempt) => ['test_complete', 'recorded', 'declined', 'rejected'].includes(attempt.state);

export function WorldpayCheckoutPanel({ payment, config, onClose, onSettled }: Props) {
  const isTry = config.environment === 'try';
  const outstanding = Math.max(0, payment.amount - (payment.manualPayment?.amountPaid || 0));
  const [amount, setAmount] = useState((isTry ? Math.min(outstanding, 0.31) : outstanding).toFixed(2));
  const [isFinal, setIsFinal] = useState(true);
  const [notes, setNotes] = useState('');
  const [holder, setHolder] = useState('');
  const [attempt, setAttempt] = useState<WorldpayCheckoutAttempt | null>(null);
  const [loaded, setLoaded] = useState(false);
  const [sdkReady, setSdkReady] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const instance = useRef<CheckoutInstance | undefined>(undefined);
  const inFlight = useRef(false);
  const alive = useRef(true);
  const completed = useRef<string | null>(null);
  const markerKey = `pcp-worldpay-checkout-sent:${payment.id}`;
  const sentId = useRef<string | null>(null);
  const formId = `worldpay-checkout-${React.useId().replace(/[^a-zA-Z0-9]/g, '')}`;

  const saveAttempt = (value: WorldpayCheckoutAttempt) => {
    // A lost response must never turn into another charge, even after reopening.
    const restored = value.state === 'ready' && sentId.current === value.id ? { ...value, state: 'unknown' } : value;
    if (terminal(value)) {
      sentId.current = null;
      try { localStorage.removeItem(markerKey); } catch { /* Backend also enforces single submission. */ }
    }
    if (!alive.current) return;
    setAttempt(restored);
    setAmount(Number(value.amountPaid).toFixed(2));
    setIsFinal(value.isFinal);
  };

  useEffect(() => {
    if (!attempt || completed.current === attempt.id) return;
    if ((attempt.state === 'recorded' && attempt.paymentRecorded) || (attempt.environment === 'try' && attempt.state === 'test_complete')) {
      completed.current = attempt.id;
      onSettled(attempt);
    }
  }, [attempt, onSettled]);

  useEffect(() => {
    alive.current = true;
    let cancelled = false;
    try { sentId.current = localStorage.getItem(markerKey); } catch { /* Recovery also uses the server. */ }
    getCheckoutAttempt(payment.id).then(result => {
      if (cancelled) return;
      if (result.attempt) saveAttempt(result.attempt);
      setLoaded(true);
    }).catch(() => { if (!cancelled) setError('The saved payment attempt could not be loaded. Close and reopen this window before taking payment.'); });
    return () => { cancelled = true; alive.current = false; };
    // The routing component remounts this component for each payment.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [payment.id]);

  useEffect(() => {
    let cancelled = false;
    let owned: CheckoutInstance | undefined;
    if (!config.checkoutId) { setError('Worldpay checkout is not configured. Contact support.'); return; }
    loadWorldpayCheckout(config.environment).then(sdk => {
      if (cancelled) return;
      sdk.checkout.init({
        id: config.checkoutId, form: `#${formId}`,
        fields: { pan: { selector: `#${formId}-pan` }, expiry: { selector: `#${formId}-expiry` }, cvv: { selector: `#${formId}-cvv` } },
        styles: { input: { 'font-size': '16px', color: '#1e293b' } },
        accessibility: { ariaLabel: { pan: 'Card number', expiry: 'Expiry date', cvv: 'Security code' }, lang: { locale: 'en-GB' }, title: { enabled: true, pan: 'Worldpay card number', expiry: 'Worldpay expiry date', cvv: 'Worldpay security code' } },
        enablePanFormatting: true,
      }, (failure, checkout) => {
        if (cancelled) { if (checkout) checkout.remove(); return; }
        if (failure) { setError('Worldpay secure card fields could not initialize. Close and reopen this window to retry.'); return; }
        owned = checkout;
        instance.current = checkout;
        setSdkReady(true);
      });
    }).catch(() => { if (!cancelled) setError('Worldpay secure card fields could not load. Close and reopen this window to retry.'); });
    return () => { cancelled = true; instance.current = undefined; if (owned) owned.remove(); };
  }, [config.checkoutId, config.environment, formId]);

  const submit = async (event: React.FormEvent) => {
    event.preventDefault();
    const amountPaid = Number(amount);
    if (inFlight.current || !loaded || !instance.current || (attempt && attempt.state !== 'ready') || !holder.trim() || !Number.isFinite(amountPaid) || amountPaid <= 0 || (amountPaid > outstanding + 0.005)) return;
    inFlight.current = true;
    setBusy(true);
    setError('');
    let active = attempt;
    let stage: 'create' | 'session' | 'pay' = 'create';
    try {
      if (!active) {
        active = (await createCheckoutAttempt(payment.id, { amountPaid, isFinal, notes: notes.trim() || undefined })).attempt;
        saveAttempt(active);
      }
      if (!alive.current || active.state !== 'ready') return;
      stage = 'session';
      const sessionHref = await new Promise<string>((resolve, reject) => {
        instance.current!.generateSessionState((failure, session) => {
          if (failure || !session) reject(new Error('session'));
          else resolve(session);
        });
      });
      if (!alive.current) return;
      stage = 'pay';
      sentId.current = active.id;
      try { localStorage.setItem(markerKey, active.id); } catch { /* Server owns the durable submission lock. */ }
      setAttempt({ ...active, state: 'submitting' });
      const result = await payCheckoutAttempt(payment.id, active.id, { sessionHref, cardHolderName: holder.trim() });
      saveAttempt(result.attempt);
      if (alive.current) { setHolder(''); instance.current?.clearForm(() => {}); }
    } catch {
      if (!alive.current) return;
      if (stage === 'session') {
        setError('Please check the card details. Worldpay could not create a secure payment session. No payment was submitted.');
      } else if (stage === 'pay' && active) {
        setAttempt({ ...active, state: 'unknown' });
        setError('The payment response was not received. Check status before taking another payment. Do not submit it again.');
      } else {
        setLoaded(false);
        setError('The payment attempt could not be prepared. Close and reopen this window to recover its saved state.');
      }
    } finally {
      inFlight.current = false;
      if (alive.current) setBusy(false);
    }
  };
  const check = async () => {
    if (!attempt || inFlight.current) return;
    inFlight.current = true; setBusy(true); setError('');
    try { saveAttempt((await checkCheckoutAttempt(payment.id, attempt.id)).attempt); }
    catch { if (alive.current) setError('Could not check Worldpay. Keep this attempt and check again; do not take another payment.'); }
    finally { inFlight.current = false; if (alive.current) setBusy(false); }
  };
  const newAttempt = () => {
    if (busy || !attempt || !terminal(attempt)) return;
    if (attempt.paymentRecorded) { onSettled(); return; }
    setAttempt(null); setError(''); setHolder('');
    instance.current?.clearForm(() => {});
  };
  const locked = busy || !!attempt;
  const canPay = loaded && sdkReady && !busy && (!attempt || attempt.state === 'ready');
  return <div className="void-modal-content worldpay-panel worldpay-checkout">
    <h3>Take card payment by phone</h3>
    <p className={isTry ? 'worldpay-checkout__test' : 'worldpay-panel__intro'}>{isTry ? 'Worldpay Try — test only. This does not credit the client’s balance or send a payment receipt. Use Worldpay test cards only.' : 'Worldpay Live — this takes a real card payment.'}</p>
    <p className="worldpay-panel__hint">{payment.clientName} · Outstanding {money(outstanding)} (inc VAT). Card details go directly to Worldpay.</p>
    {error && <div role="alert" className="admin-payments-feedback error">{error}</div>}
    {!loaded && !error && <p role="status">Loading saved payment attempt…</p>}
    <form id={formId} onSubmit={submit}>
      <div className="worldpay-panel__field"><label htmlFor={`${formId}-amount`}>Amount (GBP)</label><input id={`${formId}-amount`} type="number" min="0.01" max={isTry ? Math.min(outstanding, 100) : outstanding} step="0.01" value={amount} disabled={locked} onChange={e => { setAmount(e.target.value); }} required /></div>
      <fieldset className="worldpay-panel__payment-type" disabled={locked}><legend>Payment type</legend>
        <label><input type="radio" name={`${formId}-final`} checked={isFinal} onChange={() => setIsFinal(true)} /> Final payment</label>
        <label><input type="radio" name={`${formId}-final`} checked={!isFinal} onChange={() => setIsFinal(false)} /> Partial payment</label>
      </fieldset>
      {Number(amount) > outstanding + 0.005 && <p role="alert">Amount exceeds the outstanding balance.</p>}
      {isTry && Number(amount) > 100 && <p role="alert">Try payments are limited to £100.00.</p>}
      <div className="worldpay-panel__field"><label htmlFor={`${formId}-notes`}>Notes (optional)</label><textarea id={`${formId}-notes`} value={notes} disabled={locked} onChange={e => setNotes(e.target.value)} rows={2} /></div>
      <div className="worldpay-panel__field"><label htmlFor={`${formId}-holder`}>Cardholder name</label><input id={`${formId}-holder`} value={holder} onChange={e => setHolder(e.target.value)} disabled={!canPay} autoComplete="off" maxLength={100} required /></div>
      <div className="worldpay-panel__field"><span className="worldpay-checkout__label">Card number</span><div id={`${formId}-pan`} className="worldpay-checkout__hosted" /></div>
      <div className="worldpay-checkout__row"><div className="worldpay-panel__field"><span className="worldpay-checkout__label">Expiry date</span><div id={`${formId}-expiry`} className="worldpay-checkout__hosted" /></div><div className="worldpay-panel__field"><span className="worldpay-checkout__label">Security code</span><div id={`${formId}-cvv`} className="worldpay-checkout__hosted" /></div></div>
      {!sdkReady && <p className="worldpay-panel__hint">Secure card fields are not ready.</p>}
      {attempt && <section className="worldpay-checkout__result" aria-label="Payment result" aria-live="polite"><h4>Payment attempt</h4><dl>
        <dt>Reference</dt><dd>{attempt.reference}</dd><dt>Amount</dt><dd>{money(Number(attempt.amountPaid))}</dd><dt>State</dt><dd>{attempt.state}</dd><dt>Worldpay outcome</dt><dd>{attempt.outcome || 'Not yet returned'}</dd>
      </dl>{attempt.message && <p role={['declined', 'rejected'].includes(attempt.state) ? 'alert' : 'status'}>{attempt.message}</p>}{attempt.paymentRecorded && <p>Payment recorded in PCP.</p>}{!terminal(attempt) && attempt.state !== 'ready' && <p>Check this attempt before taking another payment.</p>}</section>}
      <div className="worldpay-panel__actions">
        <button type="button" className="worldpay-panel__button worldpay-panel__button--secondary" disabled={busy} onClick={() => attempt?.paymentRecorded ? onSettled(attempt) : onClose()}>Close</button>
        {attempt && <button type="button" className="worldpay-panel__button worldpay-panel__button--secondary" disabled={busy} onClick={check}>Check status</button>}
        {attempt && terminal(attempt) && !attempt.paymentRecorded && <button type="button" className="worldpay-panel__button worldpay-panel__button--secondary" disabled={busy} onClick={newAttempt}>{isTry ? 'New test payment' : 'New payment attempt'}</button>}
        <button type="submit" className="worldpay-panel__button worldpay-panel__button--primary" disabled={!canPay || !amount || Number(amount) <= 0 || (isTry && Number(amount) > 100) || (Number(amount) > outstanding + 0.005)}>{busy ? 'Contacting Worldpay…' : `Take ${money(Number(amount) || 0)} ${isTry ? 'test payment' : 'payment'}`}</button>
      </div>
    </form>
  </div>;
}
